import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import logo from '../traderhub24-logo.svg'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'
import Login from './components/Login'
import Register from './components/Register'
import Footer from './components/Footer'

type PageKey =
  | 'home'
  | 'changelog'
  | 'privacy-policy'
  | 'terms-and-conditions'
  | 'risk-warning'
  | 'documentation'
  | 'all-trades'
  | 'analytics'
  | 'tools'
  | 'strategies'
  | 'account'
  | 'symbol-management'
  | 'social-trade'
  | 'social-trade-trade-pools'
  | 'social-trade-followers'
  | 'community'

type TradeRow = {
  id: string
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  strategy_id: string | null
  currency: string | null
  position_size: number | null
  pnl: number | null
  pnl_percentage: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
  time_interval: string | null
  is_public: boolean
  public_slug: string | null
  public_visibility?: 'link' | 'community' | 'restricted' | null
  public_show_quantity: boolean
  trade_pool: string | null
  created_at: string
  updated_at: string
}

type TradePoolRow = {
  id: string
  name: string
  settings: any
  created_at: string
  updated_at: string
}

function formatShortDate(dateString: string | null) {
  if (!dateString) return '-'
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}

function SocialTrade({
  onBack,
  setCurrentPage,
  embedded,
}: {
  onBack: () => void
  setCurrentPage: (page: PageKey) => void
  embedded?: boolean
}) {
  const navigate = useNavigate()
  const { t } = useTranslation(['pages', 'common'])
  const { user, signOut, loading: authLoading } = useAuth()
  const { isFree, profileLoading, membershipType, canUseSocialTrade } = useMembership()
  const { showSuccess, showError, showConfirm } = useNotification()

  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<TradeRow[]>([])
  const [refreshTick, setRefreshTick] = useState(0)

  const [pools, setPools] = useState<TradePoolRow[]>([])
  const [loadingPools, setLoadingPools] = useState(false)
  const [tradesByPool, setTradesByPool] = useState<Record<string, TradeRow[]>>({})
  const [loadingTradesByPool, setLoadingTradesByPool] = useState(false)

  const [settingsTrade, setSettingsTrade] = useState<TradeRow | null>(null)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [settingsShowQuantity, setSettingsShowQuantity] = useState(false)
  const [settingsVisibility, setSettingsVisibility] = useState<'link' | 'community' | 'restricted'>('link')
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)

  const [filterMarket, setFilterMarket] = useState<'all' | TradeRow['market']>('all')
  const [filterDirection, setFilterDirection] = useState<'all' | TradeRow['long_short']>('all')
  const [search, setSearch] = useState('')
  const [showPnl, setShowPnl] = useState(true)

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  // Session control - redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      setShowLogin(true)
    }
  }, [authLoading, user])

  const fetchMyPublicTrades = async () => {
    if (!user) {
      setItems([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error: qError } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_public', true)
        .order('entry_date', { ascending: false })

      if (qError) throw qError
      setItems((data as TradeRow[]) || [])
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load public trades')
    } finally {
      setLoading(false)
    }
  }

  const fetchPools = async () => {
    if (!user) {
      setPools([])
      return
    }

    try {
      setLoadingPools(true)
      const { data, error: qError } = await supabase
        .from('trade_pools')
        .select('id, name, settings, created_at, updated_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (qError) throw qError
      setPools((data as TradePoolRow[]) || [])
    } catch (err: any) {
      showError(err.message || 'Failed to load trade pools')
    } finally {
      setLoadingPools(false)
    }
  }

  const fetchTradesByPool = async () => {
    if (!user) {
      setTradesByPool({})
      return
    }

    try {
      setLoadingTradesByPool(true)
      const { data, error: qError } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('user_id', user.id)
        .not('trade_pool', 'is', null)
        .order('entry_date', { ascending: false })

      if (qError) throw qError

      // Group trades by pool
      const grouped: Record<string, TradeRow[]> = {}
      if (data) {
        data.forEach((trade: TradeRow) => {
          if (trade.trade_pool) {
            if (!grouped[trade.trade_pool]) {
              grouped[trade.trade_pool] = []
            }
            grouped[trade.trade_pool].push(trade)
          }
        })
      }
      setTradesByPool(grouped)
    } catch (err: any) {
      showError(err.message || 'Failed to load trades by pool')
    } finally {
      setLoadingTradesByPool(false)
    }
  }

  const createDetailId = (trade: TradeRow) => {
    const uuid = crypto.randomUUID ? crypto.randomUUID() : 
      `detail-${Math.random().toString(36).slice(2, 10)}`
    return `${trade.symbol}-${uuid}`
  }

  const seededAllowHistory = (seed: string) => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i)
      hash |= 0
    }
    const ratio = (hash >>> 0) / 4294967295
    return ratio < 0.2
  }

  const buildDetailState = (trade: TradeRow) => {
    const { id: _tradeId, ...restWithUser } = trade as TradeRow & { user_id?: string }
    const { user_id: _userId, ...tradePayload } = restWithUser

    const baseDate = trade.entry_date ? new Date(trade.entry_date) : new Date()
    const entryPrice = trade.entry_price
    const exitPrice =
      trade.exit_price ??
      (trade.long_short === 'long' ? entryPrice * 1.02 : entryPrice * 0.98)

    const timeline = [
      {
        date: baseDate.toISOString(),
        title: 'Trade Opened',
        description: 'Initial entry placed; alert triggered on setup validation.',
        price: entryPrice,
        tag: 'entry',
      },
      {
        date: new Date(baseDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        title: 'Daily Review',
        description: 'Reviewed stop placement and kept risk constant.',
        price: entryPrice * (trade.long_short === 'long' ? 1.01 : 0.99),
        tag: 'review',
      },
      {
        date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        title: 'Market Event',
        description: 'Macro headline created volatility; kept discipline.',
        tag: 'macro',
      },
      {
        date: new Date(baseDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        title: 'Adjustment',
        description: 'Moved stop to breakeven after partial target touched.',
        price: entryPrice,
        tag: 'risk',
      },
      {
        date: new Date(baseDate.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString(),
        title: trade.exit_date ? 'Trade Closed' : 'Current Status',
        description: trade.exit_date
          ? 'Position exited; captured planned move.'
          : 'Position still active; monitoring key levels.',
        price: exitPrice,
        tag: trade.exit_date ? 'exit' : 'open',
      },
    ]

    const comments = [
      {
        author: 'Self-Note',
        text: 'Keep journaling intraday emotions; avoid revenge trades.',
        date: baseDate.toISOString(),
        mood: 'Focused',
      },
      {
        author: 'Market',
        text: 'Watch for liquidity sweep near prior highs before adding size.',
        date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        mood: 'Alert',
      },
    ]

    return {
      trade: tradePayload,
      timeline,
      comments,
      mood: 'Focused',
      tradeId: trade.id,
    }
  }

  const handleNavigateToDetail = (trade: TradeRow) => {
    const detailId = createDetailId(trade)
    const detailState = buildDetailState(trade)
    const historyAccessGranted =
      membershipType === 'pro' || membershipType === 'publisher'
        ? true
        : membershipType === 'free'
          ? seededAllowHistory(detailId)
          : false

    navigate(`/app/trade/${detailId}`, { state: { ...detailState, historyAccessGranted } })
  }

  useEffect(() => {
    if (user) {
      fetchMyPublicTrades()
      fetchPools()
      fetchTradesByPool()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshTick])

  const filteredItems = useMemo(() => {
    let rows = [...items]

    if (filterMarket !== 'all') {
      rows = rows.filter((t) => t.market === filterMarket)
    }
    if (filterDirection !== 'all') {
      rows = rows.filter((t) => t.long_short === filterDirection)
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase()
      rows = rows.filter((t) => t.symbol.toLowerCase().includes(s))
    }

    return rows
  }, [items, filterMarket, filterDirection, search])

  const copyShareLink = async (slug: string) => {
    const url = `${window.location.origin}/p/${slug}`
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
        showSuccess('Public link copied to clipboard')
      } else {
        window.prompt('Copy this link:', url)
      }
    } catch {
      window.prompt('Copy this link:', url)
    }
  }

  const openSettings = (trade: TradeRow) => {
    setSettingsTrade(trade)
    setSettingsShowQuantity(Boolean(trade.public_show_quantity))
    setSettingsVisibility((trade.public_visibility as 'link' | 'community' | 'restricted') || 'link')
    setSettingsError(null)
    setShowSettingsModal(true)
  }

  const saveSettings = async () => {
    if (!user || !settingsTrade) return

    try {
      setSettingsSaving(true)
      setSettingsError(null)

      const { error: uError } = await supabase
        .from('trades')
        .update({
          public_show_quantity: settingsShowQuantity,
          public_visibility: settingsVisibility,
        })
        .eq('id', settingsTrade.id)

      if (uError) throw uError

      setItems((prev) =>
        prev.map((t) =>
          t.id === settingsTrade.id
            ? {
                ...t,
                public_show_quantity: settingsShowQuantity,
                public_visibility: settingsVisibility,
              }
            : t,
        ),
      )

      setShowSettingsModal(false)
      setSettingsTrade(null)
    } catch (err: any) {
      setSettingsError(err.message || 'Failed to save settings')
    } finally {
      setSettingsSaving(false)
    }
  }

  const handleUpgrade = () => {
    setCurrentPage('home')
    setTimeout(() => {
      const el = document.getElementById('pricing')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  // If not authenticated, show login prompt (skip when embedded; layout handles auth)
  if (!user && !authLoading && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        {/* Header */}
        <header className="w-full flex justify-between items-center py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <img
              src={logo}
              alt="TraderHub24"
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onBack}
            />
          </div>
          <div className="flex items-center gap-2" />
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center mb-4">Please sign in to access Social Trade</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowLogin(true)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowRegister(true)}
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer currentPage="social-trade" onNavigate={(page) => {
          if (page === "privacy-policy") navigate("/privacy");
          else if (page === "terms-and-conditions") navigate("/terms");
          else if (page === "risk-warning") navigate("/risk-warning");
          else if (page === "documentation") navigate("/documentation");
          else if (page === "contact") navigate("/contact");
          else if (page === "home") onBack();
          else if (page === "changelog") navigate("/changelog");
        }} />

        {/* Auth Modals */}
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false)
              setShowRegister(true)
            }}
          />
        )}
        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false)
              setShowLogin(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img
            src={logo}
            alt="TraderHub24"
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            {authLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : (
              <div className="relative flex items-center gap-2">
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showMobileMenu ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    )}
                  </svg>
                </button>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                    title="Account"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span className="hidden md:inline">Account</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">Account</p>
                        <p className="text-sm text-gray-600 mt-1 truncate">{user?.email}</p>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate("/app/account")
                            setShowUserMenu(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0" />
                          </svg>
                          Account Settings
                        </button>
                        <button
                          onClick={() => {
                            signOut()
                            setShowUserMenu(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      item.id === "social-trade" 
                        ? "bg-orange-50 text-orange-600" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    navigate('/app/add-trade');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors mt-4"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Trade
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {(() => {
        const W = embedded ? "div" : "main";
        return (
        <W className={embedded ? "dashboard-page-content" : "max-w-6xl mx-auto px-4 md:px-6 py-6"}>
        {!embedded && (
        <section className="hidden md:flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => setCurrentPage("home")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
            title="Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
          <button
            onClick={() => navigate("/all-trades")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            All Trades
          </button>
          <button
            onClick={() => navigate("/app/analytics")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate("/app/tools")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Tools
          </button>
          <button
            onClick={() => navigate("/app/strategies")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Strategies
          </button>
          <button
            onClick={() => navigate("/app/social-trade")}
            className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Social Trade
          </button>
          <button
            onClick={() => navigate("/app/community")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Community
          </button>
          <button
            onClick={() => navigate('/app/add-trade')}
            className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 hover:border-orange-600 transition-colors"
          >
            Add Trade
          </button>
        </section>
        )}

        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold">{t('pages:socialTrade.title')}</h1>
              <p className="text-gray-600 mt-2">Manage your shared trades and copy share links.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => navigate("/app/social-trade/trade-pools")}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
              >
                Trade Pool
              </button>
              <button
                type="button"
                onClick={() => navigate("/app/social-trade/followers")}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
              >
                Followers
              </button>
            </div>
          </div>
        </div>

        {!canUseSocialTrade && !profileLoading && (
          <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-lg text-sm">
              {isFree ? 'Subscribe to Pro to activate this feature.' : 'This feature is disabled for your membership.'}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleUpgrade}
                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
              >
                View plans
              </button>
              <button
                type="button"
                onClick={() => navigate("/all-trades")}
                className="px-5 py-2 bg-app-box border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Go to All Trades
              </button>
            </div>
          </div>
        )}

        {canUseSocialTrade && (
          <div className="space-y-6">
            <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">My Public Trades</h2>
                  <p className="text-sm text-gray-600 mt-1">These are the trades where “Public share” is enabled.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Market</label>
                    <select
                      value={filterMarket}
                      onChange={(e) => setFilterMarket(e.target.value as any)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All</option>
                      <option value="Commodity">Commodity</option>
                      <option value="Crypto">Crypto</option>
                      <option value="Forex">Forex</option>
                      <option value="Stock">Stock</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                    <select
                      value={filterDirection}
                      onChange={(e) => setFilterDirection(e.target.value as any)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All</option>
                      <option value="long">Long</option>
                      <option value="short">Short</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Symbol</label>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="e.g. AAPL"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 mt-5 md:mt-0 select-none">
                    <input type="checkbox" checked={showPnl} onChange={(e) => setShowPnl(e.target.checked)} className="accent-orange-500" />
                    Show P&L
                  </label>
                </div>
              </div>

              {loading ? (
                <p className="text-gray-500 text-center py-10">Loading public trades...</p>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-4">{error}</div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-600">You don’t have any public trades yet.</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Go to <strong>All Trades</strong> → edit a trade → enable <strong>Public share</strong>.
                  </p>
                  <div className="mt-4 flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate("/all-trades")}
                      className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
                    >
                      Go to All Trades
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto mt-6">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Symbol</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Market</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dir</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Entry</th>
                        {showPnl && (
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">P&L</th>
                        )}
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-app-box divide-y divide-gray-200">
                      {filteredItems.map((t) => {
                        return (
                          <tr 
                            key={t.id} 
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => handleNavigateToDetail(t)}
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="font-semibold text-sm text-gray-900">{t.symbol}</div>
                              <div className="text-xs text-gray-500">{t.exit_date ? 'CLOSED' : 'OPEN'}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{t.market}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                  t.long_short === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {t.long_short.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{formatShortDate(t.entry_date)}</td>
                            {showPnl && (
                              <td className="px-3 py-2 whitespace-nowrap text-xs text-right font-semibold">
                                {t.pnl === null ? (
                                  <span className="text-gray-400">-</span>
                                ) : (
                                  <span className={t.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {t.pnl >= 0 ? '+' : ''}
                                    {t.pnl.toFixed(2)} {t.currency ?? ''}
                                  </span>
                                )}
                                {t.pnl_percentage !== null && (
                                  <div className="text-[11px] text-gray-500 mt-0.5">{t.pnl_percentage.toFixed(2)}%</div>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-2 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => t.public_slug && copyShareLink(t.public_slug)}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                                >
                                  Copy link
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openSettings(t)}
                                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-orange-500 hover:text-orange-500 transition-colors"
                                >
                                  Settings
                                </button>
                                <a
                                  href={t.public_slug ? `/p/${t.public_slug}` : '#'}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (!t.public_slug) e.preventDefault()
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium rounded-lg border text-gray-700 bg-white border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-colors"
                                >
                                  Open Public Link
                                </a>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Trade Pools Section */}
            <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Trade Pools</h2>
                <p className="text-sm text-gray-600 mt-1">Trades organized by pool.</p>
              </div>

              {loadingPools || loadingTradesByPool ? (
                <p className="text-gray-500 text-center py-10">Loading pools...</p>
              ) : pools.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-600">You don't have any trade pools yet.</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Go to <strong>Trade Pool</strong> to create a pool and add trades to it.
                  </p>
                  <div className="mt-4 flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate("/app/social-trade/trade-pools")}
                      className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
                    >
                      Go to Trade Pools
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {pools.map((pool) => {
                    const poolTrades = tradesByPool[pool.id] || []
                    return (
                      <div
                        key={pool.id}
                        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">{pool.name}</h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {poolTrades.length} {poolTrades.length === 1 ? 'trade' : 'trades'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => navigate("/app/social-trade/trade-pools")}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-orange-500 hover:text-orange-500 transition-colors"
                          >
                            Manage Pool
                          </button>
                        </div>

                        {poolTrades.length === 0 ? (
                          <div className="text-center py-6 text-sm text-gray-500">
                            No trades in this pool yet.
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Symbol</th>
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Market</th>
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dir</th>
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Entry</th>
                                  {showPnl && (
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">P&L</th>
                                  )}
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {poolTrades.map((t) => {
                                  return (
                                    <tr 
                                      key={t.id} 
                                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                                      onClick={() => handleNavigateToDetail(t)}
                                    >
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        <div className="font-semibold text-sm text-gray-900">{t.symbol}</div>
                                        <div className="text-xs text-gray-500">{t.exit_date ? 'CLOSED' : 'OPEN'}</div>
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{t.market}</td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        <span
                                          className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                            t.long_short === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                          }`}
                                        >
                                          {t.long_short.toUpperCase()}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{formatShortDate(t.entry_date)}</td>
                                      {showPnl && (
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-right font-semibold">
                                          {t.pnl === null ? (
                                            <span className="text-gray-400">-</span>
                                          ) : (
                                            <span className={t.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                                              {t.pnl >= 0 ? '+' : ''}
                                              {t.pnl.toFixed(2)} {t.currency ?? ''}
                                            </span>
                                          )}
                                          {t.pnl_percentage !== null && (
                                            <div className="text-[11px] text-gray-500 mt-0.5">{t.pnl_percentage.toFixed(2)}%</div>
                                          )}
                                        </td>
                                      )}
                                      <td className="px-3 py-2 whitespace-nowrap text-center">
                                        <span
                                          className={`px-2 py-1 rounded text-xs font-medium ${
                                            t.is_public
                                              ? 'bg-green-100 text-green-700'
                                              : 'bg-gray-100 text-gray-600'
                                          }`}
                                        >
                                          {t.is_public ? 'Public' : 'Private'}
                                        </span>
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </section>
          </div>
        )}
        </W>
        );
      })()}

      <Footer currentPage="social-trade" onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") setCurrentPage("changelog");
      }} />

      {!embedded && showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false)
            setShowRegister(true)
          }}
        />
      )}

      {!embedded && showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false)
            setShowLogin(true)
          }}
        />
      )}

      {/* Settings Modal (per public trade) */}
      {showSettingsModal && settingsTrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="rounded-2xl p-6 max-w-lg w-full mx-4 my-8 shadow-xl bg-app-box border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Public Trade Settings</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {settingsTrade.symbol} • {settingsTrade.market}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSettingsModal(false)
                  setSettingsTrade(null)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {settingsError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {settingsError}
              </div>
            )}

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg px-4 py-3 bg-white">
                <label className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">Show quantity</div>
                    <div className="text-xs text-gray-500 mt-1">
                      If enabled, the public trade page will display how many shares/units were traded.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsShowQuantity}
                    onChange={(e) => setSettingsShowQuantity(e.target.checked)}
                    className="h-4 w-4 accent-orange-500"
                  />
                </label>
              </div>

              <div className="border border-gray-200 rounded-lg px-4 py-3 bg-white">
                <label className="block text-sm font-medium text-gray-900 mb-2">Visibility</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="settingsVisibility"
                      value="link"
                      checked={settingsVisibility === 'link'}
                      onChange={() => setSettingsVisibility('link')}
                      className="h-4 w-4 accent-orange-500"
                    />
                    <span className="text-sm text-gray-700">Link</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="settingsVisibility"
                      value="community"
                      checked={settingsVisibility === 'community'}
                      onChange={() => setSettingsVisibility('community')}
                      className="h-4 w-4 accent-orange-500"
                    />
                    <span className="text-sm text-gray-700">Community</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="settingsVisibility"
                      value="restricted"
                      checked={settingsVisibility === 'restricted'}
                      onChange={() => setSettingsVisibility('restricted')}
                      className="h-4 w-4 accent-orange-500"
                    />
                    <span className="text-sm text-gray-700">Followers</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-5">
              <button
                type="button"
                onClick={() => {
                  setShowSettingsModal(false)
                  setSettingsTrade(null)
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                disabled={settingsSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveSettings}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={settingsSaving}
              >
                {settingsSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SocialTrade

